import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  BarChart3, TrendingUp, MousePointerClick, FileSignature, Eye, Users, Loader2,
  Mail, Send, Plus, X,
} from 'lucide-react';

/**
 * QuoteAnalyticsPanel — Admin overview of the insurance-quote funnel.
 * Visualises page views → tab clicks → form engagement → submissions.
 *
 * Props:
 *   apiUrl, adminPassword
 */
export default function QuoteAnalyticsPanel({ apiUrl, adminPassword }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!adminPassword) return;
    let cancel = false;
    setLoading(true);
    setErr('');
    fetch(`${apiUrl}/api/quote-analytics/stats?days=${days}`, {
      headers: { 'X-Admin-Password': adminPassword },
    })
      .then(async r => {
        if (!r.ok) throw new Error(`Stats fetch failed (${r.status})`);
        return r.json();
      })
      .then(d => { if (!cancel) setData(d); })
      .catch(e => { if (!cancel) setErr(e.message || 'Failed to load stats'); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [apiUrl, adminPassword, days]);

  if (loading) {
    return (
      <Card data-testid="quote-stats-loading">
        <CardContent className="py-10 flex items-center justify-center text-gray-500">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading quote analytics…
        </CardContent>
      </Card>
    );
  }
  if (err) {
    return (
      <Card>
        <CardContent className="py-6 text-red-700 bg-red-50">{err}</CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const f = data.funnel || {};
  const tabSplit = data.tab_split || [];
  const tabTotal = tabSplit.reduce((s, t) => s + t.count, 0) || 1;

  const cards = [
    { label: 'Page Views', value: f.page_views, sub: `${data.totals.find(t => t.event_type === 'quote_page_view')?.unique_visitors || 0} unique`, icon: Eye, color: 'text-sky-600 bg-sky-50' },
    { label: 'Tab Clicks', value: f.tab_clicks, sub: `${f.tab_click_rate_pct}% of page views`, icon: MousePointerClick, color: 'text-purple-600 bg-purple-50' },
    { label: 'Form Engagements', value: f.iframe_loads, sub: `${f.engagement_rate_pct}% of tab clicks`, icon: Users, color: 'text-amber-600 bg-amber-50' },
    { label: 'Quotes Submitted', value: f.submissions, sub: `${f.submission_rate_pct}% of engagements`, icon: FileSignature, color: 'text-green-600 bg-green-50' },
  ];

  return (
    <div className="space-y-6" data-testid="quote-analytics-panel">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-[#0077B6]" /> Insurance Quote Funnel
            </CardTitle>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-600">Last</span>
              <select
                value={days}
                onChange={e => setDays(Number(e.target.value))}
                className="border rounded px-2 py-1 text-sm"
                data-testid="quote-stats-window-select"
              >
                <option value={1}>24 hours</option>
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
                <option value={90}>90 days</option>
                <option value={365}>1 year</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Funnel cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {cards.map(c => (
              <div key={c.label} className="border rounded-lg p-4 bg-white" data-testid={`stat-card-${c.label.toLowerCase().replace(/ /g, '-')}`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs uppercase tracking-wider text-gray-500">{c.label}</p>
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${c.color}`}>
                    <c.icon className="h-4 w-4" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-gray-900 mt-2">{c.value || 0}</p>
                <p className="text-xs text-gray-500 mt-1">{c.sub}</p>
              </div>
            ))}
          </div>

          {/* Overall conversion */}
          <div className="bg-gradient-to-r from-[#003D5C] to-[#0077B6] text-white rounded-lg p-5 flex items-center justify-between mb-6">
            <div>
              <p className="text-blue-100 text-sm">Overall conversion</p>
              <p className="text-3xl font-bold">{f.overall_conversion_pct}%</p>
            </div>
            <TrendingUp className="h-10 w-10 text-[#D4AF37]" />
          </div>

          {/* Tab split */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-3 text-sm">Tab clicks by insurance type</h3>
            {tabSplit.length === 0 ? (
              <p className="text-sm text-gray-500">No tab clicks recorded yet.</p>
            ) : (
              <div className="space-y-2">
                {['life', 'critical', 'visitor'].map(name => {
                  const row = tabSplit.find(t => t.tab === name) || { count: 0 };
                  const pct = Math.round((row.count / tabTotal) * 100) || 0;
                  const label = name === 'life' ? 'Life Insurance' : name === 'critical' ? 'Critical Illness' : 'Visitors to Canada';
                  const color = name === 'life' ? 'bg-[#0077B6]' : name === 'critical' ? 'bg-rose-500' : 'bg-emerald-500';
                  return (
                    <div key={name} className="flex items-center gap-3" data-testid={`tab-stat-${name}`}>
                      <span className="text-sm font-medium text-gray-700 w-40">{label}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                        <div className={`h-full ${color}`} style={{ width: `${pct}%` }}></div>
                      </div>
                      <span className="text-sm tabular-nums text-gray-700 w-24 text-right">{row.count} ({pct}%)</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <DigestRecipientsCard apiUrl={apiUrl} adminPassword={adminPassword} />
    </div>
  );
}

// ============================================================
// Weekly Digest Recipients management
// ============================================================
function DigestRecipientsCard({ apiUrl, adminPassword }) {
  const [recipients, setRecipients] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState(null); // {type:'ok'|'err', text}

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${apiUrl}/api/admin/quote-digest/recipients`, {
        headers: { 'X-Admin-Password': adminPassword },
      });
      const d = await r.json();
      setRecipients(d.recipients || []);
    } catch { /* swallow */ }
    setLoading(false);
  };
  useEffect(() => { if (adminPassword) load();   }, [adminPassword]);

  const persist = async (next) => {
    setSaving(true);
    setMsg(null);
    try {
      const r = await fetch(`${apiUrl}/api/admin/quote-digest/recipients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Password': adminPassword },
        body: JSON.stringify({ recipients: next }),
      });
      const d = await r.json();
      setRecipients(d.recipients || []);
      setMsg({ type: 'ok', text: 'Recipients updated.' });
    } catch (e) {
      setMsg({ type: 'err', text: 'Failed to save recipients.' });
    }
    setSaving(false);
  };

  const addEmail = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      setMsg({ type: 'err', text: 'Enter a valid email.' });
      return;
    }
    if (recipients.includes(email)) {
      setMsg({ type: 'err', text: 'That email is already on the list.' });
      return;
    }
    persist([...recipients, email]);
    setNewEmail('');
  };

  const removeEmail = (email) => persist(recipients.filter(e => e !== email));

  const sendNow = async () => {
    if (!recipients.length) {
      setMsg({ type: 'err', text: 'Add at least one recipient first.' });
      return;
    }
    if (!window.confirm(`Send the current weekly digest now to ${recipients.length} recipient(s)?`)) return;
    setSending(true);
    setMsg(null);
    try {
      const r = await fetch(`${apiUrl}/api/admin/quote-digest/send-now`, {
        method: 'POST',
        headers: { 'X-Admin-Password': adminPassword },
      });
      const d = await r.json();
      if (d.sent > 0) {
        setMsg({ type: 'ok', text: `Digest sent to ${d.sent}/${d.total} recipient(s).${d.failed?.length ? ` Failed: ${d.failed.join(', ')}` : ''}` });
      } else {
        setMsg({ type: 'err', text: `Send failed. ${d.skipped || d.failed?.join(', ') || 'Unknown error.'}` });
      }
    } catch {
      setMsg({ type: 'err', text: 'Send request failed.' });
    }
    setSending(false);
  };

  return (
    <Card data-testid="digest-recipients-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-[#0077B6]" />
          Weekly Digest Email Recipients
        </CardTitle>
        <p className="text-sm text-gray-600 mt-1">
          A branded summary of the past 7 days' quote funnel is sent every <b>Monday at 08:00 America/Toronto</b> to everyone on this list.
        </p>
      </CardHeader>
      <CardContent>
        {msg && (
          <div className={`mb-4 px-3 py-2 rounded text-sm ${msg.type === 'ok' ? 'bg-green-50 text-green-800 border-l-4 border-green-500' : 'bg-red-50 text-red-800 border-l-4 border-red-500'}`}>
            {msg.text}
          </div>
        )}
        <div className="flex gap-2 mb-4">
          <input
            type="email"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            placeholder="name@example.com"
            className="flex-1 border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#0077B6]"
            onKeyDown={e => e.key === 'Enter' && addEmail()}
            data-testid="new-recipient-input"
          />
          <button
            onClick={addEmail}
            disabled={saving}
            className="px-3 py-2 bg-[#0077B6] text-white rounded hover:bg-[#005f8a] disabled:opacity-50 text-sm flex items-center gap-1"
            data-testid="add-recipient-btn"
          >
            <Plus className="h-4 w-4" /> Add
          </button>
        </div>

        {loading ? (
          <div className="text-sm text-gray-500 py-3">Loading recipients…</div>
        ) : recipients.length === 0 ? (
          <p className="text-sm text-gray-500 italic py-3">No recipients yet — add one above to start receiving weekly digests.</p>
        ) : (
          <ul className="space-y-2 mb-4" data-testid="recipients-list">
            {recipients.map(e => (
              <li key={e} className="flex items-center justify-between border rounded px-3 py-2 bg-gray-50 text-sm">
                <span className="text-gray-800">{e}</span>
                <button
                  onClick={() => removeEmail(e)}
                  className="text-red-600 hover:bg-red-50 rounded p-1"
                  title="Remove"
                  data-testid={`remove-recipient-${e}`}
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex justify-end pt-3 border-t">
          <button
            onClick={sendNow}
            disabled={sending || recipients.length === 0}
            className="px-4 py-2 bg-[#D4AF37] text-[#003D5C] font-bold rounded hover:bg-[#b8962e] disabled:opacity-50 text-sm flex items-center gap-2"
            data-testid="send-digest-now-btn"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {sending ? 'Sending…' : 'Send Test Digest Now'}
          </button>
        </div>
      </CardContent>
    </Card>
  );
}
